import { useState, useCallback } from 'react';
import { Node, Edge } from 'reactflow';
import { NodeGroup } from '../types/groupTypes';
import { getNextColor } from '../utils/groupColors';
import { findConnectedComponents } from '../utils/graphAlgorithms';
import { trainingGroupsApi } from '../services/trainingGroupsApi';
import { messagesApi } from '../services/messagesApi';

const GROUP_PADDING = 25;

// Generate next sequential numeric group_id based on existing groups
function getNextGroupId(existingGroups: NodeGroup[]): string {
  const usedIds = existingGroups
    .map(g => parseInt(g.id, 10))
    .filter(n => !isNaN(n));
  const maxId = usedIds.length > 0 ? Math.max(...usedIds) : 0;
  return String(maxId + 1);
}

export function useNodeGroups() {
  const [groups, setGroups] = useState<NodeGroup[]>([]);

  const renumberGroups = useCallback((groupsToRenumber: NodeGroup[]) => {
    const sorted = [...groupsToRenumber].sort((a, b) => a.createdAt - b.createdAt);
    return sorted.map((group, index) => ({
      ...group,
      number: index + 1,
      // id permanece intacto para mantener sincronía con training_groups en BD
    }));
  }, []);

  const calculateGroupBounds = useCallback(
    (nodeIds: string[], allNodes: Node[]) => {
      const groupNodes = allNodes.filter(node => nodeIds.includes(node.id));
      
      if (groupNodes.length === 0) {
        return { position: { x: 0, y: 0 }, dimensions: { width: 300, height: 150 } };
      }

      let minX = Infinity;
      let minY = Infinity;
      let maxX = -Infinity;
      let maxY = -Infinity;

      groupNodes.forEach(node => {
        const nodeWidth = (node.width || 250);
        const nodeHeight = (node.height || 120);
        
        minX = Math.min(minX, node.position.x);
        minY = Math.min(minY, node.position.y);
        maxX = Math.max(maxX, node.position.x + nodeWidth);
        maxY = Math.max(maxY, node.position.y + nodeHeight);
      });

      return {
        position: {
          x: minX - GROUP_PADDING,
          y: minY - GROUP_PADDING,
        },
        dimensions: {
          width: maxX - minX + GROUP_PADDING * 2,
          height: maxY - minY + GROUP_PADDING * 2,
        },
      };
    },
    []
  );

  const getGroupForNode = useCallback(
    (nodeId: string) => {
      return groups.find(group => group.nodeIds.includes(nodeId)) || null;
    },
    [groups]
  );

  const createGroupForNode = useCallback(
    (nodeId: string, allNodes: Node[]) => {
      // Verificar si el nodo ya tiene un grupo
      const existingGroup = groups.find(group => group.nodeIds.includes(nodeId));
      if (existingGroup) {
        return; // El nodo ya tiene un grupo
      }

      const bounds = calculateGroupBounds([nodeId], allNodes);
      const newGroup: NodeGroup = {
        id: getNextGroupId(groups),
        number: groups.length + 1,
        nodeIds: [nodeId],
        color: getNextColor(groups.length),
        position: bounds.position,
        dimensions: bounds.dimensions,
        createdAt: Date.now(),
      };

      setGroups(prev => renumberGroups([...prev, newGroup]));
    },
    [groups, calculateGroupBounds, renumberGroups]
  );

  const createGroupFromConnection = useCallback(
    (sourceId: string, targetId: string, allNodes: Node[]) => {
      const sourceGroup = getGroupForNode(sourceId);
      const targetGroup = getGroupForNode(targetId);

      // CASO 1: Si ambos nodos NO tienen grupo, crear uno nuevo con ambos
      if (!sourceGroup && !targetGroup) {
        const bounds = calculateGroupBounds([sourceId, targetId], allNodes);
        const newGroup: NodeGroup = {
          id: getNextGroupId(groups),
          number: groups.length + 1,
          nodeIds: [sourceId, targetId],
          color: getNextColor(groups.length),
          position: bounds.position,
          dimensions: bounds.dimensions,
          createdAt: Date.now(),
        };

        setGroups(prev => renumberGroups([...prev, newGroup]));
      } 
      // CASO 2: Source tiene grupo, target no → AGREGAR target al grupo del source
      else if (sourceGroup && !targetGroup) {
        setGroups(prev => {
          const updated = prev.map(group => {
            if (group.id === sourceGroup.id) {
              const nodeIds = [...group.nodeIds, targetId];
              const bounds = calculateGroupBounds(nodeIds, allNodes);
              return {
                ...group,
                nodeIds,
                position: bounds.position,
                dimensions: bounds.dimensions,
              };
            }
            return group;
          });
          return renumberGroups(updated);
        });
      } 
      // CASO 3: Target tiene grupo, source no → AGREGAR source al grupo del target
      else if (!sourceGroup && targetGroup) {
        setGroups(prev => {
          const updated = prev.map(group => {
            if (group.id === targetGroup.id) {
              const nodeIds = [...group.nodeIds, sourceId];
              const bounds = calculateGroupBounds(nodeIds, allNodes);
              return {
                ...group,
                nodeIds,
                position: bounds.position,
                dimensions: bounds.dimensions,
              };
            }
            return group;
          });
          return renumberGroups(updated);
        });
      }
      // CASO 4: Ambos tienen grupos diferentes → FUSIONAR GRUPOS
      else if (sourceGroup && targetGroup && sourceGroup.id !== targetGroup.id) {
        setGroups(prev => {
          // Mantener el grupo más antiguo (menor createdAt)
          const keepGroup = sourceGroup.createdAt < targetGroup.createdAt ? sourceGroup : targetGroup;
          const mergeGroup = keepGroup === sourceGroup ? targetGroup : sourceGroup;
          
          // Combinar nodos únicos
          const combinedNodeIds = [...new Set([...keepGroup.nodeIds, ...mergeGroup.nodeIds])];
          
          // Preservar la instrucción y leadStatus del grupo más antiguo
          const instruction = keepGroup.instruction || mergeGroup.instruction;
          const leadStatus = keepGroup.leadStatus || mergeGroup.leadStatus;
          
          const bounds = calculateGroupBounds(combinedNodeIds, allNodes);
          
          const updated = prev
            .filter(g => g.id !== mergeGroup.id)
            .map(group => {
              if (group.id === keepGroup.id) {
                return {
                  ...group,
                  nodeIds: combinedNodeIds,
                  instruction,
                  leadStatus,
                  position: bounds.position,
                  dimensions: bounds.dimensions,
                };
              }
              return group;
            });
          
          return renumberGroups(updated);
        });
      }
    },
    [groups, getGroupForNode, calculateGroupBounds, renumberGroups]
  );

  const removeNodeFromGroup = useCallback(
    (nodeId: string, groupId: string, allNodes: Node[], flowId?: string | null) => {
      // Eliminar mensaje de la BD
      messagesApi.deleteMessage(nodeId).catch(err => 
        console.error('Error eliminando mensaje de BD:', err)
      );

      setGroups(prev => {
        const updated = prev
          .map(group => {
            if (group.id === groupId) {
              const nodeIds = group.nodeIds.filter(id => id !== nodeId);
              
              // Si el grupo queda vacío, eliminarlo también de BD (con flowId)
              // La BD indexa por el NÚMERO del grupo, no por su id interno.
              if (nodeIds.length === 0) {
                trainingGroupsApi.deleteGroup(String(group.number), flowId).catch(err =>
                  console.error('Error eliminando grupo de BD:', err)
                );
                return null;
              }
              
              const bounds = calculateGroupBounds(nodeIds, allNodes);
              return {
                ...group,
                nodeIds,
                position: bounds.position,
                dimensions: bounds.dimensions,
              };
            }
            return group;
          })
          .filter((g): g is NodeGroup => g !== null);
        
        return renumberGroups(updated);
      });
    },
    [calculateGroupBounds, renumberGroups]
  );

  const deleteGroup = useCallback((
    groupId: string, 
    allEdges: Edge[], 
    setEdges: (edges: Edge[]) => void,
    setNodes: (nodes: Node[] | ((prev: Node[]) => Node[])) => void,
    flowId?: string | null
  ) => {
    // Obtener el grupo a eliminar
    const groupToDelete = groups.find(g => g.id === groupId);
    if (!groupToDelete) return;
    
    const orphanedNodeIds = groupToDelete.nodeIds;

    // Eliminar grupo de la BD (CON flowId para evitar eliminar grupos de otros workflows)
    // La BD indexa por el NÚMERO del grupo, no por su id interno.
    trainingGroupsApi.deleteGroup(String(groupToDelete.number), flowId).catch(err =>
      console.error('Error eliminando grupo de BD:', err)
    );

    // LÓGICA: Si tiene UN SOLO nodo, eliminar el nodo también
    if (orphanedNodeIds.length === 1) {
      const nodeId = orphanedNodeIds[0];
      
      console.log('🗑️ Eliminando grupo con 1 nodo: nodo también se elimina', nodeId);
      
      // Eliminar mensaje de BD
      messagesApi.deleteMessage(nodeId).catch(err =>
        console.error('Error eliminando mensaje de BD:', err)
      );
      
      // Eliminar nodo del canvas
      setNodes((prevNodes: Node[]) => prevNodes.filter(node => node.id !== nodeId));
      
      // Eliminar edges conectados al nodo
      setEdges(allEdges.filter((edge: Edge) => 
        edge.source !== nodeId && edge.target !== nodeId
      ));
    } else {
      // LÓGICA: Si tiene VARIOS nodos, solo eliminar grupo (separarlos)
      console.log('✂️ Eliminando grupo con', orphanedNodeIds.length, 'nodos: nodos se separan');
      
      // Eliminar conexiones entre nodos del grupo para separarlos
      const edgesToKeep = allEdges.filter((edge: Edge) => {
        const sourceInGroup = orphanedNodeIds.includes(edge.source);
        const targetInGroup = orphanedNodeIds.includes(edge.target);
        // Mantener edge solo si NO ambos están en el grupo
        return !(sourceInGroup && targetInGroup);
      });
      setEdges(edgesToKeep);
    }

    // Eliminar el grupo del estado
    setGroups(prev => {
      const updated = prev.filter(group => group.id !== groupId);
      return renumberGroups(updated);
    });
  }, [groups, renumberGroups]);

  const updateGroupColor = useCallback((groupId: string, color: string) => {
    setGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, color } : group
      )
    );
  }, []);

  const updateGroupInstruction = useCallback((groupId: string, instruction: string) => {
    setGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, instruction } : group
      )
    );
  }, []);

  const updateGroupLeadStatus = useCallback((groupId: string, leadStatus: string) => {
    setGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, leadStatus } : group
      )
    );
  }, []);

  const updateGroupProductKeys = useCallback((groupId: string, productKeys: string[]) => {
    setGroups(prev =>
      prev.map(group =>
        group.id === groupId ? { ...group, productKeys } : group
      )
    );
  }, []);

  const updateAllGroupBounds = useCallback(
    (allNodes: Node[]) => {
      setGroups(prev => {
        const updated = prev
          .map(group => {
            // Filtrar nodeIds que ya no existen en el canvas
            const existingNodeIds = group.nodeIds.filter(nid =>
              allNodes.some(n => n.id === nid)
            );

            // Si no quedan nodos, eliminar el grupo
            if (existingNodeIds.length === 0) {
              return null;
            }

            const bounds = calculateGroupBounds(existingNodeIds, allNodes);
            return {
              ...group,
              nodeIds: existingNodeIds,
              position: bounds.position,
              dimensions: bounds.dimensions,
            };
          })
          .filter((g): g is NodeGroup => g !== null);

        return renumberGroups(updated);
      });
    },
    [calculateGroupBounds, renumberGroups]
  );

  const checkGroupIntegrity = useCallback(
    (allNodes: Node[], allEdges: Edge[]) => {
      console.log('🔍 Verificando integridad de grupos...');
      console.log('   Nodos totales:', allNodes.length);
      console.log('   Edges totales:', allEdges.length);
      console.log('   Grupos actuales:', groups.length);
      
      setGroups(prev => {
        const updated: NodeGroup[] = [];
        
        prev.forEach(group => {
          console.log(`\n📦 Analizando grupo ${group.id}:`, group.nodeIds);
          
          const components = findConnectedComponents(group.nodeIds, allEdges);
          console.log(`   Componentes encontrados: ${components.length}`);
          components.forEach((comp, idx) => {
            console.log(`   - Componente ${idx + 1}:`, comp);
          });
          
          if (components.length === 0) {
            console.log('   ⚠️ Sin componentes, omitiendo grupo');
            return;
          }
          
          if (components.length === 1) {
            // Grupo mantiene integridad - actualizar bounds
            console.log('   ✅ Grupo mantiene integridad');
            const bounds = calculateGroupBounds(group.nodeIds, allNodes);
            updated.push({
              ...group,
              position: bounds.position,
              dimensions: bounds.dimensions,
            });
          } else {
            console.log('   🔀 Grupo se divide en', components.length, 'componentes');
            
            // Grupo se divide en múltiples componentes
            // El componente más grande mantiene el grupo original con su instrucción
            const largestComponent = components.reduce((largest, current) =>
              current.length > largest.length ? current : largest
            );
            
            console.log('   📌 Componente más grande:', largestComponent);
            const bounds = calculateGroupBounds(largestComponent, allNodes);
            updated.push({
              ...group,
              nodeIds: largestComponent,
              position: bounds.position,
              dimensions: bounds.dimensions,
            });
            
            // Crear NUEVOS grupos para componentes separados (incluso nodos aislados)
            components
              .filter(comp => comp !== largestComponent)
              .forEach(comp => {
                console.log('   ➕ Creando nuevo grupo para:', comp);
                const bounds = calculateGroupBounds(comp, allNodes);
                updated.push({
                  id: getNextGroupId(updated),
                  number: updated.length + 1,
                  nodeIds: comp,
                  color: getNextColor(updated.length),
                  position: bounds.position,
                  dimensions: bounds.dimensions,
                  createdAt: Date.now(),
                });
              });
          }
        });
        
        console.log('✨ Grupos resultantes:', updated.length);
        const renumbered = renumberGroups(updated);
        console.log('📋 Grupos después de renumerar:', renumbered.map(g => ({
          id: g.id,
          number: g.number,
          nodeIds: g.nodeIds
        })));
        
        return renumbered;
      });
    },
    [calculateGroupBounds, renumberGroups, groups]
  );

  return {
    groups,
    setGroups,
    createGroupForNode,
    createGroupFromConnection,
    removeNodeFromGroup,
    deleteGroup,
    updateGroupColor,
    updateGroupInstruction,
    updateGroupLeadStatus,
    updateGroupProductKeys,
    updateAllGroupBounds,
    checkGroupIntegrity,
    getGroupForNode,
  };
}
